import { SearchkitClient, SearchkitProvider, useSearchkitVariables } from '@searchkit/client';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { V } from '../../../Layout';
import type { WorkEntriesDatagridProps } from '../../types';
import {
    applyWorkEntryOrder,
    filterWorkEntryRows,
    reconcileWorkEntryOrder,
    sortWorkEntryRowsByEndDate
} from '../utils';
import WorkEntriesFooter from './internal/WorkEntriesFooter';
import WorkEntriesGrid from './internal/WorkEntriesGrid';
import WorkEntriesHeader from './internal/WorkEntriesHeader';

function WorkEntriesDatagridContent<TSource>({
    rows,
    onBulkAction,
    bulkActionLoading,
    bulkActionLabel,
    backLabel,
    onBack,
    showBackButton = true,
    compactHorizontalPadding = false,
    enableSearch = true,
    enableFilters = true,
    enableKeyQuickFilter = false,
    enableReorder = false,
    enableSelection = false,
    footerFilterMode = 'selected',
    footerFilterLabel,
    stickyFooter = true,
    minCardColumnWidth = 240,
    rowActionsEnabled = false,
    onEdit,
    onRequestDelete,
    showKeyIcon = true,
    keyEntryTooltip,
    keyQuickFilterTooltip,
    inlineAddButton,
    footerAccessory
}: WorkEntriesDatagridProps<TSource>) {
    const variables = useSearchkitVariables();

    const [localSearchQuery, setLocalSearchQuery] = useState('');
    const [showFooterFilteredOnly, setShowFooterFilteredOnly] = useState(false);
    const [keyFilterValues, setKeyFilterValues] = useState<string[]>([]);

    const [orderedIds, setOrderedIds] = useState<string[]>(() => rows.map((row) => row.id));

    const [selectedIds, setSelectedIds] = useState<Set<string>>(
        () => new Set(rows.filter((row) => row.isKey).map((row) => row.id))
    );

    const keySignature = useMemo(
        () =>
            rows
                .map((row) => `${row.id}:${row.isKey ? 1 : 0}`)
                .sort()
                .join(';'),
        [rows]
    );
    useEffect(() => {
        setSelectedIds(new Set(rows.filter((row) => row.isKey).map((row) => row.id)));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [keySignature]);

    // Keep ordered ids in sync with the work entries list (preserve manual order when reorder is enabled).
    useEffect(() => {
        if (!enableReorder) return;
        const incoming = rows.map((row) => row.id);
        setOrderedIds((prev) => reconcileWorkEntryOrder(prev, incoming));
    }, [rows, enableReorder]);

    const selectedTypeFilter = useMemo(
        () =>
            variables.filters?.filter((filter) => filter.identifier === 'type').map((filter) => String(filter.value)) ||
            [],
        [variables.filters]
    );
    const selectedYearFilter = useMemo(
        () =>
            variables.filters?.filter((filter) => filter.identifier === 'year').map((filter) => Number(filter.value)) ||
            [],
        [variables.filters]
    );
    const orderedRows = useMemo(
        () => (enableReorder ? applyWorkEntryOrder(rows, orderedIds) : sortWorkEntryRowsByEndDate(rows)),
        [rows, orderedIds, enableReorder]
    );
    const effectiveOrderedIds = useMemo(() => orderedRows.map((row) => row.id), [orderedRows]);
    const filteredRows = useMemo(
        () =>
            filterWorkEntryRows(orderedRows, {
                searchQuery: localSearchQuery,
                typeFilter: selectedTypeFilter,
                yearFilter: selectedYearFilter,
                keyFilter: enableKeyQuickFilter ? keyFilterValues : undefined,
                footerToggle: {
                    active: showFooterFilteredOnly && footerFilterMode === 'selected',
                    mode: footerFilterMode,
                    selectedIds,
                    enableSelection
                }
            }),
        [
            orderedRows,
            localSearchQuery,
            selectedTypeFilter,
            selectedYearFilter,
            keyFilterValues,
            enableKeyQuickFilter,
            showFooterFilteredOnly,
            footerFilterMode,
            selectedIds,
            enableSelection
        ]
    );

    const toggleEntry = useCallback(
        (id: string) => {
            if (!enableSelection) return;
            setSelectedIds((prev) => {
                const next = new Set(prev);
                if (next.has(id)) {
                    next.delete(id);
                } else {
                    next.add(id);
                }
                return next;
            });
        },
        [enableSelection]
    );

    const showFooter =
        Boolean(footerAccessory) ||
        showBackButton ||
        (footerFilterMode === 'selected' && enableSelection) ||
        (enableSelection && onBulkAction);

    const footer = showFooter ? (
        <WorkEntriesFooter
            rows={rows}
            orderedIds={effectiveOrderedIds}
            selectedIds={selectedIds}
            showFooterFilteredOnly={showFooterFilteredOnly}
            onToggleFooterFilter={setShowFooterFilteredOnly}
            footerFilterMode={footerFilterMode}
            footerFilterLabel={footerFilterLabel}
            enableSelection={enableSelection}
            onBulkAction={onBulkAction}
            bulkActionLoading={bulkActionLoading}
            bulkActionLabel={bulkActionLabel}
            showBackButton={showBackButton}
            backLabel={backLabel}
            onBack={onBack}
            stickyFooter={stickyFooter}
            compactHorizontalPadding={compactHorizontalPadding}
            footerAccessory={footerAccessory}
        />
    ) : null;

    const headerAndGrid = (
        <>
            <WorkEntriesHeader
                rows={rows}
                enableSearch={enableSearch}
                enableFilters={enableFilters}
                enableKeyQuickFilter={enableKeyQuickFilter}
                keyFilterValues={keyFilterValues}
                onKeyFilterChange={setKeyFilterValues}
                keyQuickFilterTooltip={keyQuickFilterTooltip}
                compactHorizontalPadding={compactHorizontalPadding}
                onSearchQueryChange={setLocalSearchQuery}
                inlineAddButton={inlineAddButton}
            />
            <WorkEntriesGrid
                rows={filteredRows}
                orderedIds={orderedIds}
                enableReorder={enableReorder}
                enableSelection={enableSelection}
                showKeyIcon={showKeyIcon}
                keyEntryTooltip={keyEntryTooltip}
                selectedIds={selectedIds}
                onToggleEntry={toggleEntry}
                onCommitOrder={setOrderedIds}
                minCardColumnWidth={minCardColumnWidth}
                compactHorizontalPadding={compactHorizontalPadding}
                rowActionsEnabled={rowActionsEnabled}
                onEdit={onEdit}
                onRequestDelete={onRequestDelete}
            />
        </>
    );

    return (
        <V
            fill
            sx={{
                width: '100%',
                flex: 1,
                minHeight: 0,
                overflow: 'hidden',
                alignItems: 'stretch'
            }}
        >
            <V sx={{ flex: 1, minHeight: 0, minWidth: 0, overflow: 'auto', width: '100%' }}>{headerAndGrid}</V>
            <V sx={{ flexShrink: 0, width: '100%' }}>{footer}</V>
        </V>
    );
}

export function WorkEntriesDatagrid<TSource>(props: WorkEntriesDatagridProps<TSource>) {
    const searchkit = useMemo(() => new SearchkitClient(), []);
    return (
        <SearchkitProvider client={searchkit}>
            <WorkEntriesDatagridContent {...props} />
        </SearchkitProvider>
    );
}
